import { supabase } from '../../lib/supabase';

export interface ChangeLogEntry {
  id: number;
  version_id: number;
  provider_id: number;
  region_id: number;
  entity_type: string;
  entity_id: number;
  field_name: string;
  action_type: 'CREATE' | 'UPDATE' | 'DELETE';
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChangesSummary {
  totalChanges: number;
  changesByEntity: Record<string, number>;
  changesByType: {
    CREATE: number;
    UPDATE: number;
    DELETE: number;
  };
}

export interface EntityChange {
  entity_type: string;
  entity_id: number;
  changes: ChangeLogEntry[];
}

export const changeTrackingQueries = {
  /**
   * Get all changes for a specific version
   */
  async getVersionChanges(versionId: number): Promise<ChangeLogEntry[]> {
    const { data, error } = await (supabase as any)
      .from('change_log')
      .select('*')
      .eq('version_id', versionId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to fetch version changes:', error);
      throw error;
    }

    return (data || []) as ChangeLogEntry[];
  },

  /**
   * Get changes for a specific entity type within a version
   */
  async getEntityChanges(
    versionId: number,
    entityType: string
  ): Promise<ChangeLogEntry[]> {
    const { data, error } = await (supabase as any)
      .from('change_log')
      .select('*')
      .eq('version_id', versionId)
      .eq('entity_type', entityType)
      .order('entity_id', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to fetch entity changes:', error);
      throw error;
    }

    return (data || []) as ChangeLogEntry[];
  },

  /**
   * Get changes for a specific entity (e.g., specific channel)
   */
  async getSpecificEntityChanges(
    versionId: number,
    entityType: string,
    entityId: number
  ): Promise<ChangeLogEntry[]> {
    const { data, error } = await (supabase as any)
      .from('change_log')
      .select('*')
      .eq('version_id', versionId)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to fetch specific entity changes:', error);
      throw error;
    }

    return (data || []) as ChangeLogEntry[];
  },

  /**
   * Get summary statistics for version changes
   */
  async getChangesSummary(versionId: number): Promise<ChangesSummary> {
    const { data, error } = await (supabase as any)
      .from('change_log')
      .select('entity_type, action_type')
      .eq('version_id', versionId);

    if (error) {
      console.error('Failed to fetch changes summary:', error);
      throw error;
    }

    const changes = (data || []) as Array<{entity_type: string, action_type: 'CREATE' | 'UPDATE' | 'DELETE'}>;
    const totalChanges = changes.length;

    const changesByEntity: Record<string, number> = {};
    const changesByType = { CREATE: 0, UPDATE: 0, DELETE: 0 };

    for (const change of changes) {
      // Count by entity type
      changesByEntity[change.entity_type] = (changesByEntity[change.entity_type] || 0) + 1;

      // Count by action type
      changesByType[change.action_type]++;
    }

    return {
      totalChanges,
      changesByEntity,
      changesByType,
    };
  },

  /**
   * Get changes grouped by entity for better display
   */
  async getGroupedEntityChanges(
    versionId: number,
    entityType: string
  ): Promise<EntityChange[]> {
    const changes = await this.getEntityChanges(versionId, entityType);

    // Group changes by entity_id
    const groupedChanges: Record<number, ChangeLogEntry[]> = {};

    for (const change of changes) {
      if (!groupedChanges[change.entity_id]) {
        groupedChanges[change.entity_id] = [];
      }
      groupedChanges[change.entity_id].push(change);
    }

    // Convert to array format
    return Object.entries(groupedChanges).map(([entityId, changes]) => ({
      entity_type: entityType,
      entity_id: parseInt(entityId, 10),
      changes,
    }));
  },

  /**
   * Get recent changes across all versions (for dashboard overview)
   */
  async getRecentChanges(
    providerId: number,
    regionId?: number,
    limit: number = 50
  ): Promise<ChangeLogEntry[]> {
    let query = (supabase as any)
      .from('change_log')
      .select('*')
      .eq('provider_id', providerId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (regionId) {
      query = query.eq('region_id', regionId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Failed to fetch recent changes:', error);
      throw error;
    }

    return (data || []) as ChangeLogEntry[];
  },

  /**
   * Check if a specific entity has changes in the version
   */
  async hasEntityChanges(
    versionId: number,
    entityType: string,
    entityId: number
  ): Promise<boolean> {
    const { data, error } = await (supabase as any)
      .from('change_log')
      .select('id')
      .eq('version_id', versionId)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .limit(1);

    if (error) {
      console.error('Failed to check entity changes:', error);
      return false;
    }

    return (data?.length || 0) > 0;
  },

  /**
   * Get changes between two versions (for comparison)
   */
  async getVersionComparison(
    oldVersionId: number,
    newVersionId: number
  ): Promise<{
    oldChanges: ChangeLogEntry[];
    newChanges: ChangeLogEntry[];
  }> {
    const [oldChanges, newChanges] = await Promise.all([
      this.getVersionChanges(oldVersionId),
      this.getVersionChanges(newVersionId),
    ]);

    return {
      oldChanges,
      newChanges,
    };
  },
};